<i18n>
[
    "browse__viewCloset--emptyCloset",
    "browse__startBrowsingTile--emptyCloset",
]
</i18n>

<template>
    <div v-if="shouldShowProductTray" class="c-closeted-products-tray" :style="style">
        <ProductTray
            :id="'closeted-products-tray'"
            :listId="listId"
            :listName="listName"
            :contentModuleId="contentModuleId"
            :contentTitle="contentTitle"
            :products="products"
            :headerLink="closetHeaderLink"
            :headerTitle="headerTitle"
            :headerSubtitle="headerSubtitle"
            :totalProducts="totalProducts"
            :mobilePeek="mobilePeek"
            :mobilePeekPercent="15"
            :mediumPeekPercent="25"
            :displayCounter="displayCounter"
            :desktopSlideSize="desktopSlideSize"
            :xlDesktopSlideSize="xlDesktopSlideSize"
            :mediumSlideSize="mediumSlideSize"
            :mobileSlideSize="mobileSlideSize"
            :translucentButtons="translucentButtons"
            :lightButtons="lightButtons"
            :fillEmptyPageWithBlankTiles="true"
            :catalogResponseId="catalogResponseId"
            :showProductInfo="showProductInfo"
            :analyticsType="analyticsType"
            :isFullBleedDesktop="true"
            :faceOutImage="faceOutImage"
            :hasEnteredViewport="hasEnteredViewport"
            :emptyTile="{
                illustration: ClosetIllustration,
            }"
            :isLoaded="isLoaded"
            @toggle-save-product-clicked="toggleClosetStatus"
        />
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { getClosetItems } from '~routes/closet/js/closet-api';

import { useGetters } from '~coreModules/core/js/composables/vuexHelpers';
import { useR15Svc } from '~coreModules/core/js/composables/useR15Svc';
import { useLogger } from '~coreModules/core/js/composables/useLogger';

import { SITE_SETTINGS_MODULE_NAME } from '~modules/site-settings/js/site-settings-store';
import { rentalRouteNames } from '~modules/core/js/router-constants';
import { CAROUSEL_IDS } from '~modules/analytics/js/analytics-constants';

import productTrayProps from '~coreModules/core/js/ui/productTrayProps';
import ProductTray from '~modules/core/components/ui/ProductTray.vue';

import ClosetIllustration from '~images/illustration__closet.png';

const props = defineProps({
    ...productTrayProps,
    maxProducts: {
        type: Number,
        default: 15,
    },
    paddingBottom: {
        type: String,
        default: null,
    },
    hasEnteredViewport: {
        type: Boolean,
        default: false,
    },
});

const { t } = useI18n();
const r15Svc = useR15Svc();
const logger = useLogger();

const products = ref([]);
const catalogResponseId = ref(null);
const isLoaded = ref(false);

const analyticsType = CAROUSEL_IDS.CLOSETED_PRODUCTS_TRAY;

const { loggedIn } = useGetters(['loggedIn']);
const { catalogFilterWhitelist } = useGetters(SITE_SETTINGS_MODULE_NAME, ['catalogFilterWhitelist']);

const shouldShowProductTray = computed(() => loggedIn.value);
const totalProducts = computed(() => products.value.length);

const showEmptyContentSlot = computed(() => props.hasEnteredViewport && isLoaded.value && !totalProducts.value);

const closetHeaderLink = computed(() => (showEmptyContentSlot.value ?
    {
        linkPath: {
            name: rentalRouteNames.browse,
            params: { slug: 'all' },
        },
        linkTitle: t('browse__startBrowsingTile--emptyCloset'),
    } :
    {
        linkPath: { name: rentalRouteNames.closet },
        linkTitle: t('browse__viewCloset--emptyCloset'),
    }));

const style = computed(() => (props.paddingBottom ? { paddingBottom: props.paddingBottom } : {}));

async function fetchClosetItems() {
    try {
        const res = await getClosetItems({
            r15Svc,
            query: {},
            catalogFilterWhitelist: catalogFilterWhitelist.value,
            logger,
        });
        products.value = res.products || [];
        catalogResponseId.value = res.responseId;
    } catch (error) {
        logger.debugError('Failed to fetch closet items for closet: \n', error);
    } finally {
        isLoaded.value = true;
    }
}

function toggleClosetStatus(index) {
    products.value.splice(index, 1);
}

watch(() => props.hasEnteredViewport, async (newValue, oldValue) => {
    if (shouldShowProductTray.value && !oldValue && newValue) {
        await fetchClosetItems();
    }
}, { immediate: true });
</script>

<style lang="scss">

</style>
